import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { useEffect, useRef, useState } from "react";
import { api, unwrap } from "../client";
import { qk } from "../keys";
import { streamSSE } from "../sse";
import type { LogLine, QueryOf } from "../types";

export type LogFilter = QueryOf<"queryLogs">;
export type LogTailFilter = QueryOf<"tailLogs">;
export type MetricsFilter = QueryOf<"queryMetrics">;

/** Historical log search. Log content is customer data (x-guv-untrusted): render as text, never as HTML. */
export function useLogs(project: string, filter: LogFilter = {}) {
  return useQuery({
    queryKey: qk.logs(project, filter),
    queryFn: () => unwrap(api.GET("/v1/projects/{project}/logs", { params: { path: { project }, query: filter } })),
    placeholderData: keepPreviousData,
    enabled: !!project,
  });
}

export function useMetrics(project: string, filter: MetricsFilter) {
  return useQuery({
    queryKey: qk.metrics(project, filter),
    queryFn: () =>
      unwrap(api.GET("/v1/projects/{project}/metrics", { params: { path: { project }, query: filter } })),
    placeholderData: keepPreviousData,
    refetchInterval: 60_000,
    enabled: !!project,
  });
}

export interface LogTailState {
  lines: LogLine[];
  connected: boolean;
  dropped: number;
  error: unknown;
  clear: () => void;
}

/**
 * Live tail over SSE (events: log, dropped, heartbeat). Keeps the newest `max` lines.
 * Pass `enabled: false` to pause (closes the stream).
 */
export function useLogTail(
  project: string,
  filter: LogTailFilter = {},
  opts: { enabled?: boolean; max?: number } = {},
): LogTailState {
  const { enabled = true, max = 2000 } = opts;
  const [lines, setLines] = useState<LogLine[]>([]);
  const [connected, setConnected] = useState(false);
  const [dropped, setDropped] = useState(0);
  const [error, setError] = useState<unknown>(null);
  const key = JSON.stringify(filter);
  const maxRef = useRef(max);
  maxRef.current = max;

  useEffect(() => {
    if (!enabled || !project) return;
    const ac = new AbortController();
    const qs = new URLSearchParams(
      Object.entries(filter).filter(([, v]) => v != null && v !== "") as [string, string][],
    ).toString();
    let pending: LogLine[] = [];
    let raf = 0;
    const flush = () => {
      raf = 0;
      const batch = pending;
      pending = [];
      setLines((prev) => {
        const next = prev.concat(batch);
        return next.length > maxRef.current ? next.slice(next.length - maxRef.current) : next;
      });
    };
    void streamSSE<LogLine | { count: number }>(
      `/v1/projects/${encodeURIComponent(project)}/logs/tail${qs ? `?${qs}` : ""}`,
      {
        signal: ac.signal,
        onOpen: () => {
          setConnected(true);
          setError(null);
        },
        onError: (e) => {
          setConnected(false);
          setError(e);
        },
        onMessage: (m) => {
          if (m.event === "log") {
            pending.push(m.data as LogLine);
            if (!raf) raf = requestAnimationFrame(flush);
          } else if (m.event === "dropped") {
            setDropped((d) => d + ((m.data as { count: number }).count ?? 0));
          }
        },
      },
    ).finally(() => setConnected(false));
    return () => {
      ac.abort();
      if (raf) cancelAnimationFrame(raf);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [project, key, enabled]);

  return { lines, connected, dropped, error, clear: () => setLines([]) };
}
